'use client'

import { m, useReducedMotion } from 'motion/react'
import Link from 'next/link'
import { useRef } from 'react'

import { GuestDoor } from '@/features/landing/door/guest-door'
import {
    type DoorPlace,
    onScreen,
    useDoorPlace,
    useRailHoldsRest,
} from '@/features/landing/rail/use-hero-door-above'
import { cn } from '@/lib/utils'

// Spec §6: the rail door is the hero's door handed up — the rail holds
// it whenever neither the hero's nor the colophon's door is on screen.
// It appears once the hero door has scrolled under the rail and yields
// only to the colophon's door; and from xl (`holdsRest`) it is up at
// rest while the hero's door is still under the fold, so a short
// laptop's first paint has one whole door rather than a cut one. It
// never hides over a gap; the ground's own label hides instead, so one
// door is on screen.
export function railDoorVisible({
    heroDoor,
    holdsRest,
    colophonDoorVisible,
}: {
    heroDoor: DoorPlace
    holdsRest: boolean
    colophonDoorVisible: boolean
}): boolean {
    if (colophonDoorVisible) return false
    if (heroDoor === 'above') return true
    return holdsRest && heroDoor !== 'visible'
}

// The Index underline's spring in, quicker ease-in out — the same two
// curves for the box and the door. The two never move at once: Sign in
// slides only while the door is out of sight. On the way in the box
// opens first and the door rises into the room it made; on the way out
// the door drops first and the box closes behind it.
const RISE = { duration: 0.36, ease: [0.22, 1, 0.36, 1] } as const
const DROP = { duration: 0.2, ease: 'easeIn' } as const
const CLOSE = { ...DROP, delay: DROP.duration } as const

// The rail's end: Sign in and the door share the row. The door's box is
// zero wide while the door is absent, so Sign in sits at the frame's
// edge; the box opens before the door rises and closes after it drops,
// and Sign in slides with it. On phones the slot is shared outright: a
// 360px rail holds the wordmark and the door, not a third item beside
// them, so the two occupy one cell and trade places through the rail's
// bottom rule — Sign in drops out, the door rises; the door drops, Sign
// in rises back — and nothing moves sideways.
export function RailDoor() {
    const rowRef = useRef<HTMLDivElement>(null)
    const heroDoor = useDoorPlace('hero-door')
    const holdsRest = useRailHoldsRest(rowRef)
    // "On screen" is judged below the opaque rail, not the viewport's top.
    const colophonDoorVisible = onScreen(useDoorPlace('colophon-door'))
    const reduce = useReducedMotion()

    const visible = railDoorVisible({
        heroDoor,
        holdsRest,
        colophonDoorVisible,
    })
    const transition = reduce ? { duration: 0 } : visible ? RISE : CLOSE
    // The token is the rest-hold's breakpoint, declared here in CSS and
    // read by the hook, so no viewport hook enters.
    return (
        <div
            ref={rowRef}
            className="grid h-16 items-center justify-items-end sm:flex xl:[--rail-holds-rest:1]"
        >
            {/* Below sm, Sign in's cell clips its drop to the rail and the
                padding keeps the focus ring inside the clip. Its visibility
                follows the move — hidden once it has dropped, shown as it
                starts back up — so the link is never focusable out of
                sight. From sm it is still, and slides with the box. */}
            <div className="flex h-16 items-center max-sm:col-start-1 max-sm:row-start-1 max-sm:overflow-hidden">
                <Link
                    href="/signin"
                    data-testid="rail-sign-in"
                    className={cn(
                        'inline-flex items-center gap-1 font-serif text-lp-nav tracking-wide text-muted-fg italic transition-[color,translate,visibility] hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-vermilion max-sm:p-1.5 max-sm:motion-reduce:transition-[color,opacity,visibility] max-sm:motion-reduce:delay-0 max-sm:motion-reduce:duration-200',
                        visible
                            ? 'max-sm:invisible max-sm:translate-y-[150%] max-sm:duration-200 max-sm:ease-in max-sm:motion-reduce:translate-y-0 max-sm:motion-reduce:opacity-0'
                            : 'max-sm:delay-200 max-sm:duration-[360ms] max-sm:ease-[cubic-bezier(.22,1,.36,1)]'
                    )}
                >
                    Sign in
                    <span aria-hidden>→</span>
                </Link>
            </div>
            {/* The box clips the rise to the rail; from sm its width is
                what moves Sign in, and below sm it keeps its own width
                (the important override outranks the inline motion value)
                in the cell it shares with Sign in. The inner padding keeps
                the focus ring inside the clip and, from sm, holds the
                row's gap. */}
            <m.div
                aria-hidden={!visible}
                inert={!visible}
                initial={false}
                animate={{ width: visible ? 'auto' : 0 }}
                transition={transition}
                className="flex h-16 shrink-0 items-center overflow-hidden max-sm:col-start-1 max-sm:row-start-1 max-sm:w-auto!"
            >
                {/* The hand-over made literal: the hero's door passes under
                    the rail's bottom rule and this one rises out of it, on
                    the Index underline's spring, once Sign in has dropped
                    (below sm) or the box has opened; it drops back down,
                    quicker, toward whichever door below has the screen, and
                    Sign in rises or the box closes after it; reduced motion
                    fades instead, with no wait. */}
                <div
                    className={cn(
                        'py-1 pr-1.5 pl-1 sm:pr-1 sm:pl-6',
                        visible
                            ? 'translate-y-0 transition-transform delay-200 duration-[360ms] ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-opacity motion-reduce:delay-0 motion-reduce:duration-200 sm:delay-[360ms]'
                            : 'translate-y-[150%] transition-transform duration-200 ease-in motion-reduce:translate-y-0 motion-reduce:opacity-0 motion-reduce:transition-opacity motion-reduce:duration-200'
                    )}
                >
                    {/* Below sm the door is the strip row it replaces, in a
                        word: "Begin", a size down. A 360px rail's row is
                        320px, the wordmark 219 of it, and "Try frame 01" at
                        the smallest size still worth a button needs 99; the
                        wordmark is not to wrap (spec §6). From sm the full
                        line fits beside Sign in. */}
                    <GuestDoor className="inline-flex cursor-pointer items-center gap-2 bg-vermilion px-3 py-2.5 text-lp-cta-sm tracking-lp-8 whitespace-nowrap text-white uppercase transition-[background,transform] duration-150 hover:bg-vermilion-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vermilion active:translate-y-px disabled:opacity-60 sm:px-4 sm:text-lp-cta">
                        <span className="sm:hidden">Begin</span>
                        <span className="hidden sm:inline">
                            Try frame 01 — no sign-in
                        </span>
                    </GuestDoor>
                </div>
            </m.div>
        </div>
    )
}
